import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { DrawingModule } from "../types/DrawingWeb";
import type { DrawingModuleType } from "../DrawingSingleton";
import { deleteObj } from "../core/delete";
import { openAs } from "../core/oda";
import { OpenMode, DuplicateRecordCloning } from "../core/constants";
import BlockLibrary from "@/blocks/library";
import { isDynamicBlockDefinition } from "./dynamic";
import { listAttributeDefinitions } from "./attributes";

/** One block definition of a database, as listed in the drawer's block tabs. */
export interface DwgBlockInfo {
  name: string;
  /** True when the definition is a dynamic block (evaluation graph). */
  isDynamic: boolean;
  /** True when the definition carries at least one editable (non-constant) ATTDEF. */
  hasAttributes: boolean;
}

/**
 * Core: list user-defined blocks from any OdDbDatabase.
 * Excludes anonymous, layout, and DR-managed blocks.
 */
export function listBlocksFromDatabase(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
): DwgBlockInfo[] {
  const blocks: DwgBlockInfo[] = [];

  const blockTableId = db.getBlockTableId();
  const blockTable = openAs(
    blockTableId,
    module.OdDbBlockTable,
    OpenMode.kForRead,
  );

  let iterator: DrawingModule.OdDbSymbolTableIterator | null = null;
  try {
    iterator = blockTable.newIterator(true, true);

    while (!iterator.done()) {
      let record: DrawingModule.OdDbSymbolTableRecord | null = null;
      let blockRecord: DrawingModule.OdDbBlockTableRecord | null = null;
      try {
        record = iterator.getRecord(OpenMode.kForRead, false);
        blockRecord = module.OdDbBlockTableRecord.cast(record);

        if (
          blockRecord &&
          !blockRecord.isAnonymous() &&
          !blockRecord.isLayout()
        ) {
          const nameStr = blockRecord.getName();
          const name = nameStr.toString();
          deleteObj(nameStr);

          if (!BlockLibrary.isDRBlock(name)) {
            const recordId = blockRecord.objectId();
            blocks.push({
              name,
              isDynamic: isDynamicBlockDefinition(module, recordId),
              hasAttributes:
                listAttributeDefinitions(module, blockRecord).length > 0,
            });
            deleteObj(recordId);
          }
        }
      } catch (e) {
        console.warn(
          "[dwg-query] listBlocksFromDatabase: error reading block record",
          e,
        );
      } finally {
        if (blockRecord) deleteObj(blockRecord);
        if (record) deleteObj(record);
      }
      iterator.step(true, true);
    }
  } finally {
    if (iterator) deleteObj(iterator);
    deleteObj(blockTable);
  }

  return blocks;
}

/**
 * List user-defined blocks from the active drawing's database.
 */
export function listDwgBlocks(viewer: DRDrawingViewer): DwgBlockInfo[] {
  if (!viewer.database) return [];
  return listBlocksFromDatabase(viewer.module, viewer.database);
}

/**
 * Returns the OdDbObjectId of a block by its raw DWG name.
 * Returns null if the block is not found in the block table.
 */
export function getDwgBlockId(
  viewer: DRDrawingViewer,
  name: string,
): DrawingModule.OdDbObjectId | null {
  const module = viewer.module;
  const db = viewer.database;
  if (!db) return null;

  const blockTableId = db.getBlockTableId();
  const blockTable = openAs(
    blockTableId,
    module.OdDbBlockTable,
    OpenMode.kForRead,
  );

  const nameStr = new module.OdString(name);
  const exists = blockTable.has(nameStr);

  if (!exists) {
    deleteObj(nameStr);
    deleteObj(blockTable);
    return null;
  }

  const blockRecord = blockTable.getAt(nameStr, OpenMode.kForRead, false);
  const blockId = blockRecord.objectId();

  deleteObj(nameStr);
  deleteObj(blockRecord);
  deleteObj(blockTable);

  return blockId;
}

/**
 * Copy a single block definition from sourceDb into targetDb.
 * Uses wblockCloneObjects — the block must exist by name in sourceDb.
 * Returns false if the block is not found.
 */
export function importBlockIntoDatabase(
  module: DrawingModuleType,
  sourceDb: DrawingModule.OdDbDatabase,
  targetDb: DrawingModule.OdDbDatabase,
  blockName: string,
): boolean {
  const blockTableId = sourceDb.getBlockTableId();
  const blockTable = openAs(
    blockTableId,
    module.OdDbBlockTable,
    OpenMode.kForRead,
  );

  const nameStr = new module.OdString(blockName);
  const exists = blockTable.has(nameStr);

  if (!exists) {
    deleteObj(nameStr);
    deleteObj(blockTable);
    return false;
  }

  const blockRecord = blockTable.getAt(nameStr, OpenMode.kForRead, false);
  const blockId = blockRecord.objectId();
  deleteObj(nameStr);
  deleteObj(blockRecord);
  deleteObj(blockTable);

  const ids = new module.OdDbObjectIdArray();
  ids.push_back(blockId);

  const idMap = module.OdDbIdMapping.createObject();
  idMap.setDestDb(targetDb);

  sourceDb.wblockCloneObjects(
    ids,
    targetDb.getBlockTableId(),
    idMap,
    DuplicateRecordCloning.kDrcIgnore,
    false,
  );

  deleteObj(ids);
  deleteObj(idMap);

  return true;
}
